import React, { useEffect } from "react";

import { ChevronRight, LayoutTemplate } from "lucide-react";

import { DEFAULT_VIEW_ID, VIEW_REGISTRY } from "./registry";
import PageHeader from "../../Components/PageHeader";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchXFactorOrgs } from "../../Store/Reducers/XFactorBriefingSlice";
import {
  setXFactorFreshnessDays,
  setXFactorOrg,
  setXFactorView,
} from "../../Store/Reducers/XFactorViewsSlice";

const FRESHNESS_OPTIONS = [
  { value: 1, label: "1 day" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

/**
 * Temp Views Rendering — EB's port of xfactor's views-rendering surface.
 *
 * The host: pick an organization, pick a view, set the freshness window, and
 * the selected view plug-in renders below. Public Data only — see
 * `registry.ts` for why the KPI views are absent.
 */
const TempViewsRendering: React.FC = () => {
  const dispatch = useAppDispatch();
  const orgs = useAppSelector(s => s.xfactorBriefing.orgs);
  const { viewId, freshnessDays, orgId, accountBriefings } = useAppSelector(
    s => s.xfactorViews
  );

  useEffect(() => {
    dispatch(fetchXFactorOrgs());
  }, [dispatch]);

  useEffect(() => {
    if (!orgId && orgs.items.length) {
      dispatch(setXFactorOrg(orgs.items[0].id));
    }
  }, [dispatch, orgId, orgs.items]);

  const [controlsOpen, setControlsOpen] = React.useState(true);

  const view =
    VIEW_REGISTRY.find(v => v.id === viewId) ||
    VIEW_REGISTRY.find(v => v.id === DEFAULT_VIEW_ID) ||
    VIEW_REGISTRY[0];
  const ViewComponent = view.component;

  return (
    <div className="flex w-full flex-col">
      <PageHeader
        title="Temp Views Rendering"
        icon={LayoutTemplate}
        subTitle="Account portfolio and briefing artifacts, rendered from Business Health briefings."
      />

      <div className="mx-3 mb-4 rounded-lg border border-gray-200 bg-white md:mx-4">
        {/* Collapsible controls. Collapsed, the inputs hide but the rendered
            view stays mounted — matching xfactor, where the chevron frees the
            viewport for the view rather than tearing it down. */}
        <button
          type="button"
          onClick={() => setControlsOpen(o => !o)}
          aria-expanded={controlsOpen}
          className="flex w-full items-center gap-2 px-4 py-3 text-left"
        >
          <ChevronRight
            size={16}
            className="text-gray-400 transition-transform duration-200"
            style={{ transform: controlsOpen ? "rotate(90deg)" : "none" }}
            aria-hidden
          />
          <span className="text-sm font-semibold text-gray-900">
            Views Rendering
          </span>
          <span className="text-xs text-gray-400">· {view.name}</span>
        </button>

        <div className={controlsOpen ? "px-4 pb-4" : "hidden"}>
          <p className="mb-4 text-xs text-gray-500">
            Choose a view and its inputs, and render it here. Views are plug-ins
            registered in{" "}
            <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px]">
              Pages/TempViewsRendering/registry.ts
            </code>{" "}
            and can be reused anywhere in the app.
          </p>

          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_200px_180px]">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                View
              </span>
              <select
                value={view.id}
                onChange={e => dispatch(setXFactorView(e.target.value))}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ad46ff]"
              >
                {VIEW_REGISTRY.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] leading-snug text-gray-400">
                {view.description}
              </span>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                Organization
              </span>
              <select
                value={orgId || ""}
                onChange={e => dispatch(setXFactorOrg(e.target.value))}
                disabled={orgs.loading || !orgs.items.length}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ad46ff] disabled:bg-gray-50"
              >
                {!orgs.items.length ? (
                  <option value="">
                    {orgs.loading ? "Loading…" : "No organizations"}
                  </option>
                ) : null}
                {orgs.items.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">
                Freshness window
              </span>
              <select
                value={freshnessDays}
                onChange={e =>
                  dispatch(setXFactorFreshnessDays(Number(e.target.value)))
                }
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#ad46ff]"
              >
                {FRESHNESS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-[11px] leading-snug text-gray-400">
                Briefings older than the window are marked stale.
              </span>
            </label>
          </div>

          {accountBriefings.error ? (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {accountBriefings.error}
            </div>
          ) : null}
        </div>

        <div className="border-t border-gray-100 px-4 py-5">
          <ViewComponent />
        </div>
      </div>
    </div>
  );
};

export default TempViewsRendering;
